import * as React from 'react';

import { Box, useTheme } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { GetServerSideProps, NextPage } from 'next';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { handleError, is500 } from '@/logic/shared';

import { AxiosError } from 'axios';
import { DynamicPagesTypes } from '../constants/dynamic-pages-enum';
import Seo from '@/components/seo/seo';
import { SeoPagesEnum } from '@/constants/seo-pages-enum';
import { findSeoInsidePage } from '@/logic/helpers/find-seo-inside-page';
import getLangHeaders from '@/logic/helpers/get-lang-headers';
import { pxToRem } from '@/theme/typography';
import { useGetDynamicPage } from '@/logic/services';
import { useGetPagesSeoMeta } from '@/logic/services/seo';
import ImageBoxFuture from '@/components/shared/image-box-future';
import loadIntlMessages from '@/logic/helpers/loadIntlMessages';

const AboutUs: NextPage<{
  path: string;
}> = ({ path }) => {
  const theme = useTheme();

  const intl = useIntl();
  const { data } = useGetDynamicPage({
    query: { type: DynamicPagesTypes.aboutUs },
  });

  const { data: seoDataList } = useGetPagesSeoMeta();
  const seoPage = findSeoInsidePage(seoDataList?.pageMetaData, SeoPagesEnum.aboutUs);

  return (
    <>
      <Seo
        title={seoPage?.titleTag || intl.formatMessage({ id: 'dynamic_pages.about_us' })}
        canonical={seoPage?.canonicalURL}
        pagePath={path}
        description={
          seoPage?.descriptionTag ||
          `${intl.formatMessage({
            id: 'dynamic_pages.about_us.description_row1',
          })} ${intl.formatMessage({ id: 'dynamic_pages.about_us.description_row2' })}`
        }
        image={data?.photoUrl}
        hasFollow={seoPage?.hasFollow}
        hasIndex={seoPage?.hasIndex}
      />
      <Box
        component='div'
        sx={{
          width: '100%',
          height: pxToRem(500),
          position: 'relative',
          [theme.breakpoints.down('tablet')]: {
            height: pxToRem(166),
          },
        }}
      >
        <ImageBoxFuture
          img={data?.photoUrl}
          title={intl.formatMessage({
            id: 'dynamic_pages.about_us',
          })}
          objectFit='cover'
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: '0% 0% 50% 50%',
            position: 'absolute',
            top: 0,
            overflow: 'hidden',
          }}
        />
        <Box
          component='section'
          sx={{
            position: 'relative',
            zIndex: 2,
            color: theme.palette.common.white,
            ml: pxToRem(128),
            mt: pxToRem(35),

            [theme.breakpoints.down('tablet')]: {
              ml: pxToRem(30),
              mt: pxToRem(16),
            },
          }}
        >
          <Box
            component='h1'
            sx={{
              fontSize: pxToRem(42),
              mb: pxToRem(20),
              pt: pxToRem(20),
              [theme.breakpoints.down('tablet')]: {
                fontSize: pxToRem(16),
              },
            }}
          >
            <FormattedMessage id='dynamic_pages.about_us' />
          </Box>
          <Box
            component='p'
            sx={{
              fontSize: pxToRem(38),
              lineHeight: pxToRem(40),
              [theme.breakpoints.down('tablet')]: {
                fontSize: pxToRem(14),
                lineHeight: pxToRem(24),
              },
            }}
          >
            <FormattedMessage id='dynamic_pages.about_us.description_row1' />
            <br />
            <FormattedMessage id='dynamic_pages.about_us.description_row2' />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          marginBlockEnd: 5,
          background: theme.palette.layout.bgSecondary,
          maxWidth: pxToRem(1102),
          mx: 'auto',
          mt: pxToRem(63),
          mb: pxToRem(42),
          px: pxToRem(140),
          py: pxToRem(32),
          borderRadius: pxToRem(25),
          fontSize: pxToRem(24),
          [theme.breakpoints.down(1134)]: {
            m: pxToRem(16),
          },
          [theme.breakpoints.down('tablet')]: {
            fontSize: pxToRem(16),
            p: pxToRem(16),
            mb: pxToRem(51),
          },
        }}
      >
        <Box
          component='section'
          dangerouslySetInnerHTML={{ __html: data?.content as string }}
          sx={{
            [theme.breakpoints.down('tablet')]: {
              fontSize: '16px !important',
              '& span,p,a,q,div,b ,strong ,i ,em ,mark ,small,del,ins ,sub,sup': {
                fontSize: '16px !important',
              },
              '& h1 ,h2': {
                fontSize: '18px !important',
                '& span': {
                  fontSize: '18px !important',
                },
              },
              '& h3 ,h4': {
                fontSize: '17px !important',
                '& span': {
                  fontSize: '17px !important',
                },
              },
            },
          }}
        />
      </Box>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async context => {
  const headers = {
    ...getLangHeaders(context.locale),
  };
  const { locale, defaultLocale } = context;
  const queryClient = new QueryClient();

  try {
    await useGetDynamicPage.fetchQuery(queryClient, {
      query: { type: DynamicPagesTypes.aboutUs },
      headers,
    });
  } catch (error: unknown) {
    if (is500(error)) {
      return handleError(error as AxiosError);
    }
  }

  try {
    await useGetPagesSeoMeta.fetchQuery(queryClient, {
      headers,
    });
  } catch (error: unknown) {
    if (is500(error)) {
      return handleError(error as AxiosError);
    }
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      path: `${context.locale}${context.resolvedUrl}`,
      intlMessages: await loadIntlMessages(locale as string, defaultLocale),
    },
  };
};

export default AboutUs;
